import React, { useState, useEffect, useMemo } from 'react';
import { Users, Pencil, Trash2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // ⬅ Add this at the top
import PopupModel from '../../../components/popup-model/PopupModel';

import './employee.css';
import {
  getActiveEmployees,
  updateEmployee,
  changeUserStatus,
  getAssignedCandidates,
} from '../../../services/admin/api';

const ActiveEmployee = () => {
  const [employees, setEmployees] = useState([]);
  const [assignedCandidates, setAssignedCandidates] = useState([]);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const [modalInfo, setModalInfo] = useState({
  show: false,
  title: '',
  message: '',
  isSuccess: true,
});


  const [editFormData, setEditFormData] = useState({
    id: '',
    name: '',
    email: '',
    contact: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchEmployees = async () => {
    try {
      const data = await getActiveEmployees();
      setEmployees(data);
    } catch (error) {
      setFeedbackMessage('❌ Failed to load employees.');
      triggerFeedback();
    }
  };

  const fetchAssignedCandidates = async (employeeId) => {
    try {
      const res = await getAssignedCandidates(employeeId);
      setAssignedCandidates(res.data);
    } catch (error) {
      setFeedbackMessage('❌ Failed to load assigned candidates.');
      triggerFeedback();
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleEdit = (employee) => {
    setEditFormData({
      id: employee.id,
      name: employee.name,
      email: employee.email,
      contact: employee.contact,
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDeactivate(employee);
    setShowConfirmModal(true);
  };

  const confirmDeactivation = async () => {
    try {
      const response = await changeUserStatus(employeeToDeactivate.id, false);
      await fetchEmployees();
      setFeedbackMessage('✅ ' + (response.message || 'Employee deactivated successfully.'));
    } catch (error) {
      setFeedbackMessage('❌ Failed to deactivate employee.');
    } finally {
      setEmployeeToDeactivate(null);
      setShowConfirmModal(false); // close confirmation modal only after API completes
      triggerFeedback();
    }
  };

  const triggerFeedback = () => {
    setShowFeedbackModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    const nameRegex = /^[a-zA-Z\s]{1,40}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const contactRegex = /^\d{10}$/;

    if (!nameRegex.test(editFormData.name)) {
      setFeedbackMessage('❌ Invalid name format.');
      return triggerFeedback();
    }

    if (!emailRegex.test(editFormData.email) || editFormData.email.length > 40) {
      setFeedbackMessage('❌ Invalid email address.');
      return triggerFeedback();
    }

    if (!contactRegex.test(editFormData.contact)) {
      setFeedbackMessage('❌ Contact number must be exactly 10 digits.');
      return triggerFeedback();
    }

    const payload = {
      username: editFormData.name,
      email: editFormData.email,
      phonenumber: editFormData.contact,
      role: 'EMPLOYEE',
    };

    try {
      const res = await updateEmployee(editFormData.id, payload);
      setFeedbackMessage('✅ ' + (res.message || 'Employee updated successfully.'));
      setShowEditModal(false);
      fetchEmployees();
    } catch (error) {
      setFeedbackMessage('❌ Failed to update employee.');
    } finally {
      triggerFeedback();
    }
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) =>
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredEmployees.slice(start, start + itemsPerPage);
  }, [filteredEmployees, currentPage]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  return (
    <div className="admin-active-employee-container">
      <div className="admin-employee-table-wrapper">
        <div className="admin-search-bar-container">
          <div className="admin-search-input-wrapper">
            <input
              type="text"
              placeholder="Search employee by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="admin-search-input"
            />
            <button
              type="button"
              className="admin-search-icon-btn"
              onClick={() => {
                setSearchTerm(searchText);
                setCurrentPage(1);
              }}
            >
              <Search size={18} />
            </button>
          </div>
        </div>

        <table className="admin-employee-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Assigned Candidates</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.id}</td>
                <td>{employee.name}</td>
                <td>{employee.email}</td>
                <td>{employee.contact}</td>
                <td>
                    <button
                      className="admin-candidate-list-btn"
                      title="View Assigned Candidates"
                      onClick={() => navigate(`/admin/employee/${employee.id}/candidates`, {
                        state: { employeeName: employee.name } // Optional: pass extra info like name
                      })}
                    >
                      <Users size={18} />
                    </button>
                  </td>
                <td>
                  <div className="admin-action-btn-group">
                    <button
                      title="Edit"
                      onClick={() => handleEdit(employee)}
                      className="admin-action-btn"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      title="Delete"
                      onClick={() => handleDeleteClick(employee)}
                      className="admin-action-btn delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-pagination-controls">
        <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
          Prev
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
          Next
        </button>
      </div>

      {/* Assigned Candidates Modal
      {showCandidateModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h3>Assigned Candidates for {selectedEmployee?.name}</h3>
            {assignedCandidates.length > 0 ? (
              <ul className="admin-assigned-list">
                {assignedCandidates.map((cand) => (
                  <li key={cand.id}>
                    <strong>{cand.fullName}</strong> – {cand.email} ({cand.phone})
                  </li>
                ))}
              </ul>
            ) : (
              <p>No candidates assigned to this employee.</p>
            )}
            <div className="admin-modal-buttons">
              <button onClick={() => setShowCandidateModal(false)} className="admin-assign-cancel">
                Close
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <div className="admin-modal-header">
              <h3>Edit Employee</h3>
              <button className="admin-close-icon" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleEditFormSubmit} className="admin-edit-form">
              <div className="admin-form-row">
                <label>ID</label>
                <input type="text" value={editFormData.id} name="id" disabled className="admin-disabled-input" />
              </div>
              <div className="admin-form-row">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-form-row">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-form-row">
                <label>Contact</label>
                <input
                  type="text"
                  name="contact"
                  value={editFormData.contact}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-modal-buttons">
                <button type="submit" className="admin-assign-confirm">Save</button>
                <button type="button" onClick={() => setShowEditModal(false)} className="admin-assign-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Deactivation Modal */}
      {showConfirmModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h3>Confirm Deactivation</h3>
            <p>Are you sure you want to deactivate {employeeToDeactivate?.name}?</p>
            <div className="admin-modal-buttons">
              <button onClick={confirmDeactivation} className="admin-assign-confirm">Yes</button>
              <button onClick={() => setShowConfirmModal(false)} className="admin-assign-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <p style={{ textAlign: 'center', fontSize: '16px' }}>{feedbackMessage}</p>
            <div className="admin-popup-modal-buttons">
              <button onClick={() => setShowFeedbackModal(false)} className="admin-popup-ok-btn">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveEmployee;
